import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "./ui";
import { locale, t } from "@/lib/i18n";
import { broadcastChannels, broadcastStatuses, type Broadcast } from "@/mocks/broadcasts";

/* Две проекции списка рассылок: таблица и календарь по датам отправки. */

const channelIcon = (id: string) => broadcastChannels.find((c) => c.id === id)?.icon ?? "ri-message-3-line";
const channelLabel = (id: string) => broadcastChannels.find((c) => c.id === id)?.label ?? id;
const statusMeta = (id: string) => broadcastStatuses.find((s) => s.id === id);

const fmt = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
};

const num = (n: number) => n.toLocaleString(locale);

export function BroadcastTable({ items, onOpen }: { items: Broadcast[]; onOpen: (b: Broadcast) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[42rem] border-collapse text-start">
        <thead>
          <tr className="border-b border-border text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            <th className="px-3 py-2 text-start font-semibold">{t("Название")}</th>
            <th className="px-3 py-2 text-start font-semibold">{t("Статус")}</th>
            <th className="px-3 py-2 text-start font-semibold">{t("Каналы")}</th>
            <th className="px-3 py-2 text-end font-semibold">{t("Получатели")}</th>
            <th className="px-3 py-2 text-start font-semibold">{t("Дата")}</th>
            <th className="px-3 py-2 text-start font-semibold">{t("Автор")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => {
            const meta = statusMeta(b.status);
            return (
              <tr
                key={b.id}
                onClick={() => onOpen(b)}
                className="cursor-pointer border-b border-border/60 transition last:border-0 hover:bg-muted/60"
              >
                <td className="max-w-[16rem] px-3 py-2.5">
                  <span className="block truncate text-[12.5px] font-semibold">{b.name}</span>
                  {b.labels.length > 0 ? (
                    <span className="mt-1 flex flex-wrap gap-1">
                      {b.labels.slice(0, 2).map((l) => (
                        <span key={l} className="rounded-full bg-primary/[.08] px-1.5 py-0.5 text-[9.5px] font-semibold text-primary">
                          {l}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="mt-0.5 block text-[10px] text-muted-foreground">{t("Без меток")}</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-semibold", meta?.pill)}>
                    <span className={cn("size-1.5 rounded-full", meta?.dot)} aria-hidden />
                    {meta?.label}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="flex items-center gap-1">
                    {b.channels.map((ch) => (
                      <span key={ch} title={channelLabel(ch)} className="grid size-6 place-items-center rounded-btn border border-border text-[11px] leading-none text-muted-foreground">
                        <i className={channelIcon(ch)} aria-hidden />
                      </span>
                    ))}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-end text-[12px] tabular-nums text-foreground/80">{b.audience > 0 ? num(b.audience) : "—"}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-[12px] tabular-nums text-muted-foreground">{fmt(b.date)}</td>
                <td className="max-w-[9rem] truncate px-3 py-2.5 text-[12px] text-muted-foreground">{b.author}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"].map((m) => t(m));
const WEEK = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((w) => t(w));

export function BroadcastCalendar({ items, onOpen }: { items: Broadcast[]; onOpen: (b: Broadcast) => void }) {
  const [cursor, setCursor] = useState({ y: 2026, m: 9 });

  const byDate = useMemo(() => {
    const map = new Map<string, Broadcast[]>();
    items.forEach((b) => {
      const list = map.get(b.date) ?? [];
      map.set(b.date, [...list, b]);
    });
    return map;
  }, [items]);

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const start = (first.getDay() + 6) % 7; // понедельник — первый столбец
    const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const out: { day: number | null; iso: string }[] = [];
    for (let i = 0; i < start; i += 1) out.push({ day: null, iso: "" });
    for (let d = 1; d <= days; d += 1) {
      const iso = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      out.push({ day: d, iso });
    }
    while (out.length % 7 !== 0) out.push({ day: null, iso: "" });
    return out;
  }, [cursor]);

  const shift = (delta: number) => {
    setCursor((prev) => {
      const m = prev.m + delta;
      if (m < 0) return { y: prev.y - 1, m: 11 };
      if (m > 11) return { y: prev.y + 1, m: 0 };
      return { y: prev.y, m };
    });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <p className="text-[12.5px] font-semibold">
          {MONTHS[cursor.m]} {cursor.y}
        </p>
        <span className="text-[10.5px] text-muted-foreground">{t("{n} по текущему фильтру", { n: items.length })}</span>
        <div className="ms-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => shift(-1)}
            aria-label={t("Предыдущий месяц")}
            className="grid size-6 cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:border-primary/50 hover:text-primary"
          >
            <ChevronLeft className="size-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => shift(1)}
            aria-label={t("Следующий месяц")}
            className="grid size-6 cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:border-primary/50 hover:text-primary"
          >
            <ChevronRight className="size-3.5" aria-hidden />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-border bg-background/40">
        {WEEK.map((w) => (
          <span key={w} className="px-1.5 py-1 text-center text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {w}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((c, i) => {
          const events = c.iso ? byDate.get(c.iso) ?? [] : [];
          return (
            <div
              key={`${c.iso}-${i}`}
              className={cn(
                "min-h-[5.2rem] border-b border-e border-border/60 p-1.5 last:border-e-0",
                c.day ? "bg-card/40" : "bg-background/30",
                (i + 1) % 7 === 0 && "border-e-0",
              )}
            >
              {c.day ? (
                <>
                  <span className={cn("inline-grid size-5 place-items-center rounded-full text-[10.5px] tabular-nums", events.length > 0 ? "bg-primary/10 font-semibold text-primary" : "text-muted-foreground")}>
                    {c.day}
                  </span>
                  <div className="mt-1 flex flex-col gap-0.5">
                    {events.slice(0, 2).map((e) => {
                      const meta = statusMeta(e.status);
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => onOpen(e)}
                          title={e.name}
                          className="flex w-full cursor-pointer items-center gap-1 rounded-[4px] border border-border/70 bg-card px-1 py-0.5 text-start transition hover:border-primary/50"
                        >
                          <span className={cn("size-1 flex-none rounded-full", meta?.dot)} aria-hidden />
                          <span className="min-w-0 truncate text-[9px] font-medium leading-relaxed">{e.name}</span>
                        </button>
                      );
                    })}
                    {events.length > 2 ? <span className="px-1 text-[9px] text-muted-foreground">+{events.length - 2}</span> : null}
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}